import { Box, Spinner as ChakraSpinner, Flex, HStack, Tag, Text } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { FiChevronDown, FiSearch, FiX } from 'react-icons/fi'

/**
 * MultiSelect Component
 * Multi-select with search, pagination, and tags display
 *
 * @param {string} label - Field label
 * @param {string} placeholder - Placeholder text
 * @param {Array} value - Selected values (array of IDs or objects)
 * @param {Function} onChange - Change handler (items) => void
 * @param {Function} fetchOptions - Async function to fetch options
 * @param {Function} getOptionLabel - Get label from item (item) => string
 * @param {Function} getOptionValue - Get value from item (item) => string|number
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {string} emptyText - Text when no results
 */
export function MultiSelect({
  label,
  placeholder = 'Обрати...',
  value = [],
  onChange,
  fetchOptions,
  getOptionLabel = (item) => item.name || item.title,
  getOptionValue = (item) => item.id,
  error,
  disabled = false,
  emptyText = 'Нічого не знайдено',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState([])
  const [selectedItems, setSelectedItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)

  const wrapperRef = useRef(null)
  const listRef = useRef(null)
  const searchInputRef = useRef(null)
  const requestIdRef = useRef(0)

  const PAGE_SIZE = 20

  // Sync selectedItems with value prop
  useEffect(() => {
    if (!value || value.length === 0) {
      setSelectedItems([])
      return
    }

    // Handle array of objects or array of IDs
    if (typeof value[0] === 'object') {
      setSelectedItems(value)
    } else {
      // If we have IDs, we need full objects to display - keep previous selectedItems
      // In real scenario, parent should pass full objects
      setSelectedItems(prev => {
        const prevMap = new Map(prev.map(item => [getOptionValue(item), item]))
        return value.map(id => prevMap.get(id) || { id }).filter(item => item)
      })
    }
  }, [value])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch options
  async function loadOptions(searchQuery = '', pageNum = 1, append = false, requestId) {
    try {
      setLoading(true)
      const result = await fetchOptions({
        search: searchQuery,
        page: pageNum,
        page_size: PAGE_SIZE,
      })

      // Ignore stale responses
      if (requestId !== requestIdRef.current) {
        return
      }

      const newOptions = result.data || []
      const hasNextPage = result.meta?.pagination?.has_next || false

      setOptions(prev => append ? [...prev, ...newOptions] : newOptions)
      setHasMore(hasNextPage)
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return
      }
      setOptions([])
      setHasMore(false)
    } finally {
      setLoading(false)
    }
  }

  // Load options when opened or search changes
  useEffect(() => {
    if (!isOpen) return

    setOptions([])
    setPage(1)

    requestIdRef.current += 1
    const currentRequestId = requestIdRef.current

    const timeoutId = setTimeout(() => {
      loadOptions(search, 1, false, currentRequestId)
    }, 150)

    return () => clearTimeout(timeoutId)
  }, [isOpen, search])

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isOpen])

  // Load more when scrolled to bottom
  function handleScroll(e) {
    const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 50
    if (bottom && hasMore && !loading) {
      const nextPage = page + 1
      setPage(nextPage)
      requestIdRef.current += 1
      loadOptions(search, nextPage, true, requestIdRef.current)
    }
  }

  // Toggle item selection
  function handleToggle(item) {
    const itemValue = getOptionValue(item)
    const isSelected = selectedItems.some(s => getOptionValue(s) === itemValue)

    let newItems
    if (isSelected) {
      newItems = selectedItems.filter(s => getOptionValue(s) !== itemValue)
    } else {
      newItems = [...selectedItems, item]
    }

    setSelectedItems(newItems)
    onChange(newItems)
  }

  // Remove item from selection
  function handleRemove(item, e) {
    e.stopPropagation()
    const itemValue = getOptionValue(item)
    const newItems = selectedItems.filter(s => getOptionValue(s) !== itemValue)
    setSelectedItems(newItems)
    onChange(newItems)
  }

  // Check if item is selected
  function isSelected(item) {
    const itemValue = getOptionValue(item)
    return selectedItems.some(s => getOptionValue(s) === itemValue)
  }

  return (
    <Box position="relative" ref={wrapperRef} opacity={disabled ? 0.6 : 1} pointerEvents={disabled ? 'none' : 'auto'}>
      {label && (
        <Text fontSize="sm" fontWeight="500" color="#172B4D" mb={1}>
          {label}
        </Text>
      )}

      {/* Selected Items Display */}
      <Box
        border="1px solid"
        borderColor={error ? '#DE350B' : isOpen ? '#0052CC' : '#DFE1E6'}
        borderRadius="sm"
        bg="white"
        minH="38px"
        cursor="pointer"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        p={2}
        transition="all 0.15s"
        _hover={{ borderColor: error ? '#DE350B' : '#B3BAC5' }}
      >
        <Flex justify="space-between" align="center" gap={2} flexWrap="wrap">
          <Flex flex={1} gap={1} flexWrap="wrap" minH="22px" align="center">
            {selectedItems.length > 0 ? (
              selectedItems.map(item => (
                <Tag.Root
                  key={getOptionValue(item)}
                  size="sm"
                  bg="#DEEBFF"
                  color="#0052CC"
                  borderRadius="sm"
                >
                  <Tag.Label>{getOptionLabel(item)}</Tag.Label>
                  <Tag.EndElement>
                    <FiX
                      size={14}
                      onClick={(e) => handleRemove(item, e)}
                      style={{ cursor: 'pointer' }}
                    />
                  </Tag.EndElement>
                </Tag.Root>
              ))
            ) : (
              <Text fontSize="sm" color="#8993A4">
                {placeholder}
              </Text>
            )}
          </Flex>
          <FiChevronDown
            size={16}
            color="#5E6C84"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
            }}
          />
        </Flex>
      </Box>

      {/* Dropdown */}
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg="white"
          border="1px solid #DFE1E6"
          borderRadius="sm"
          boxShadow="0 4px 12px rgba(0,0,0,0.1)"
          zIndex={1000}
          maxH="300px"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          {/* Search Input */}
          <Box p={2} borderBottom="1px solid #DFE1E6">
            <Flex align="center" gap={2}>
              <FiSearch color="#8993A4" size={16} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Пошук..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontSize: '14px',
                  color: '#172B4D',
                }}
              />
            </Flex>
          </Box>

          {/* Options List */}
          <Box
            ref={listRef}
            overflowY="auto"
            flexGrow={1}
            onScroll={handleScroll}
          >
            {loading && options.length === 0 ? (
              <Flex justify="center" align="center" p={4}>
                <ChakraSpinner size="sm" color="#0052CC" />
              </Flex>
            ) : options.length === 0 ? (
              <Text fontSize="sm" color="#8993A4" p={3} textAlign="center">
                {emptyText}
              </Text>
            ) : (
              <>
                {options.map((option) => {
                  const selected = isSelected(option)
                  return (
                    <Box
                      key={getOptionValue(option)}
                      p={2}
                      cursor="pointer"
                      bg={selected ? '#DEEBFF' : 'white'}
                      color={selected ? '#0052CC' : '#172B4D'}
                      fontSize="sm"
                      _hover={{ bg: selected ? '#B3D4FF' : '#F4F5F7' }}
                      onClick={() => handleToggle(option)}
                      transition="background 0.15s"
                    >
                      <HStack justify="space-between">
                        <Text>{getOptionLabel(option)}</Text>
                        {selected && (
                          <Box color="#0052CC" fontSize="16px" fontWeight="bold">
                            ✓
                          </Box>
                        )}
                      </HStack>
                    </Box>
                  )
                })}
                {loading && (
                  <Flex justify="center" p={2}>
                    <ChakraSpinner size="xs" color="#0052CC" />
                  </Flex>
                )}
              </>
            )}
          </Box>
        </Box>
      )}

      {/* Error Message */}
      {error && (
        <Text fontSize="xs" color="#DE350B" mt={1}>
          {error}
        </Text>
      )}
    </Box>
  )
}
